/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  FluentProvider,
  Input,
  SearchBox,
  Select,
  Skeleton,
  SkeletonItem,
  Tab,
  TabList,
  webLightTheme,
} from "@fluentui/react-components";
import {
  Add16Regular,
  Add20Regular,
  ArrowDownload20Regular,
  ArrowSwap20Regular,
  ArrowUndo20Regular,
  Box20Regular,
  Building20Regular,
  Calculator20Regular,
  Cart20Regular,
  Checkmark20Regular,
  ChevronDown12Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  ChevronUp12Regular,
  Clock20Regular,
  DataBarVertical20Regular,
  Delete16Regular,
  Delete20Regular,
  Desktop20Regular,
  Dismiss16Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Food20Regular,
  Gift20Regular,
  Globe20Regular,
  History20Regular,
  Home20Regular,
  Link20Regular,
  Money20Regular,
  Navigation20Regular,
  PanelLeft20Regular,
  Pause20Regular,
  Payment20Regular,
  People20Regular,
  Person20Regular,
  Receipt20Regular,
  ShoppingBag20Regular,
  Subtract16Regular,
  Tag20Regular,
  TagMultiple20Regular,
  Tv20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import styles from "./Retails.module.css";
import foodImage from "../../assets/images/Snail.avif";
import retailsLogo from "../../assets/images/Retail.svg";
import { cashApi, crud, dashboard, errorMessage, orderApi, saleApi } from "./api/retailApi";
import type { OrderBoardCard, PaymentInput, Query, SaleDto } from "./api/retailApi";
import { useRetailEvents } from "../../lib/useRetailEvents";
import type { RetailEvent } from "../../lib/useRetailEvents";

/* =====================================================================
   Types
   ===================================================================== */
type Row = { id: string; [k: string]: any };
interface Opt {
  v: string;
  l: string;
}
type Lk = "products" | "stores" | "categories" | "suppliers" | "customers" | "registers" | "promotions" | "jurisdictions" | "taxCategories" | "associates";
type LkMap = Partial<Record<Lk, Map<string, string>>>;
type Kind = "money" | "date" | "dt" | "status" | "num" | "pct" | "mono" | "bool" | "lookup";

interface Col {
  key: string;
  label: string;
  kind?: Kind;
  lookup?: Lk;
  get?: (r: Row, lk: LkMap) => any;
  right?: boolean;
}
interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "date" | "datetime" | "select";
  options?: Opt[];
  lookup?: Lk;
  required?: boolean;
  full?: boolean;
  pct?: boolean;
}
interface Device {
  storeId: string;
  registerId: string;
}
interface Ctx {
  currency: string;
  device: Device | null;
  setDevice: (d: Device | null) => void;
  say: (msg: string, err?: boolean) => void;
  go: (id: string) => void;
}
interface Act {
  label: string;
  icon: React.ReactNode;
  show: (r: Row) => boolean;
  ask?: { label: string; kind: "text" | "number" };
  run: (r: Row, c: Ctx, answer?: string) => Promise<string>;
}
interface Entity {
  id: string;
  group: string;
  title: string;
  subtitle: string;
  noun: string;
  icon: React.ReactNode;
  page: "grid" | "pos" | "dash" | "board" | "reports";
  route?: string;
  /** entity class name, used to refresh the list when the server pushes a change */
  type?: string;
  cols: Col[];
  fields: Fld[];
  acts: Act[];
  readOnly?: boolean;
  status?: { key: string; options: Opt[] };
  byStore?: boolean;
  defaults?: (c: Ctx) => Record<string, any>;
  lookups?: Lk[];
  validate?: (v: Record<string, any>) => string | null;
  Detail?: React.FC<{ row: Row }>;
}

/* =====================================================================
   Helpers
   ===================================================================== */
const PAGE_SIZE = 25;
const DEVICE_KEY = "retail.device";

let CURRENCY = "NGN";
const setCurrency = (c: string) => {
  if (c) CURRENCY = c;
};
const formats = new Map<string, Intl.NumberFormat>();
const money = (n: number, cur: string = CURRENCY) => {
  let f = formats.get(cur);
  if (!f) {
    try {
      f = new Intl.NumberFormat("en-NG", { style: "currency", currency: cur });
    } catch {
      f = new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" });
    }
    formats.set(cur, f);
  }
  return f.format(n || 0);
};

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
const off = (n: number) => {
  const dt = new Date();
  dt.setDate(dt.getDate() + n);
  return iso(dt);
};
// the API sends UTC times without a trailing Z, so add it before parsing
const parseUtc = (s: string) => new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
const fdate = (s?: string | null) => {
  if (!s) return "";
  if (s.includes("T")) return parseUtc(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y || 0, (m || 1) - 1, d || 1).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fdt = (s?: string | null) => (s ? parseUtc(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const minutesSince = (s: string) => Math.max(0, Math.round((Date.now() - parseUtc(s).getTime()) / 60000));
const num = (v: any): number => (typeof v === "number" ? v : Number(v) || 0);
const short = (id: any) => (id ? String(id).slice(0, 8) : "");
const cap = (s: string) => `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}`;
const spaced = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1 $2");
const st = (...a: string[]): Opt[] => a.map((v) => ({ v, l: spaced(v) }));
const ACTIVE_OPTS: Opt[] = [
  { v: "true", l: "Active" },
  { v: "false", l: "Inactive" },
];
const positive = (key: string, label: string) => (v: Record<string, any>) => (num(v[key]) > 0 ? null : `${label} must be greater than zero.`);

const TONE: Record<string, string> = {
  Completed: "green", Paid: "green", Active: "green", Open: "green", Approved: "green", Posted: "green", Received: "green", Reconciled: "green", Fulfilled: "green", Ready: "green", "In stock": "green",
  Suspended: "amber", Pending: "amber", PendingApproval: "amber", Requested: "amber", Investigating: "amber", OnHold: "amber", Medium: "amber", Low: "blue", "Low stock": "amber",
  Shipped: "blue", Sourced: "blue", Authorized: "blue", Confirmed: "blue", PartiallyFulfilled: "blue", PartiallyReceived: "blue", Preparing: "blue", Picking: "blue",
  Failed: "red", Blocked: "red", Expired: "red", Rejected: "red", Discrepancy: "red", Critical: "red", High: "red", "Out of stock": "red",
};
const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${styles.pill} ${styles["tone_" + (TONE[text] || "grey")]}`}>{spaced(text)}</span>;

const LOOKUPS: Record<Lk, { route: string; label: (r: Row) => string }> = {
  products: { route: "catalog/products", label: (r) => String(r.name) },
  stores: { route: "organization/stores", label: (r) => String(r.name) },
  categories: { route: "catalog/categories", label: (r) => String(r.name) },
  suppliers: { route: "procurement/suppliers", label: (r) => String(r.name) },
  customers: { route: "customers/customers", label: (r) => String(r.displayName) },
  registers: { route: "organization/registers", label: (r) => String(r.name) },
  promotions: { route: "pricing/promotions", label: (r) => String(r.name) },
  jurisdictions: { route: "tax/jurisdictions", label: (r) => String(r.name) },
  taxCategories: { route: "tax/categories", label: (r) => String(r.name) },
  associates: { route: "organization/associates", label: (r) => String(r.displayName) },
};

const val = (c: Col, r: Row, lk: LkMap): any => (c.get ? c.get(r, lk) : r[c.key]);
const show = (c: Col, v: any, lk: LkMap): string => {
  if (v === null || v === undefined || v === "") return "";
  switch (c.kind) {
    case "money":
      return money(num(v));
    case "date":
      return fdate(String(v));
    case "dt":
      return fdt(String(v));
    case "pct":
      return `${num(v)}%`;
    case "num":
      return num(v).toLocaleString("en-NG");
    case "bool":
      return v ? "Yes" : "No";
    case "lookup":
      return (c.lookup && lk[c.lookup]?.get(String(v))) || short(v);
    default:
      return String(v);
  }
};

/* =====================================================================
   Data hooks
   ===================================================================== */
function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    let live = true;
    setLoading(true);
    fnRef
      .current()
      .then((d) => {
        if (live) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => {
        if (live) setError(errorMessage(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload };
}

/** Reloads when the server pushes a matching event (debounced, so a burst of changes causes one reload). */
function useLive(match: (e: RetailEvent) => boolean, reload: () => void) {
  const timer = useRef<number | undefined>(undefined);
  const matchRef = useRef(match);
  const reloadRef = useRef(reload);
  matchRef.current = match;
  reloadRef.current = reload;
  useRetailEvents((e) => {
    if (!matchRef.current(e)) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => reloadRef.current(), 500);
  });
  useEffect(() => () => window.clearTimeout(timer.current), []);
}

function useLookups(keys: Lk[]) {
  const k = keys.join(",");
  const { data } = useLoad<LkMap>(async () => {
    const out: LkMap = {};
    await Promise.all(
      keys.map(async (key) => {
        const def = LOOKUPS[key];
        const page = await crud<Row>(def.route).list({ pageSize: 200 });
        out[key] = new Map(page.items.map((r) => [r.id, def.label(r)] as [string, string]));
      }),
    );
    return out;
  }, [k]);
  return data ?? {};
}

const RetailContext = createContext<Ctx>(null as unknown as Ctx);
const useCtx = () => useContext(RetailContext);

/* =====================================================================
   Skeletons
   ===================================================================== */
type SkSize = 8 | 12 | 16 | 20 | 24 | 28 | 32 | 36 | 40 | 48 | 56 | 64 | 72 | 96 | 120 | 128;
const SkLine: React.FC<{ w?: string; size?: SkSize }> = ({ w = "100%", size = 16 }) => (
  <Skeleton aria-label="Loading" style={{ width: w }}>
    <SkeletonItem size={size} />
  </Skeleton>
);
const TableSkeleton: React.FC<{ cols: number; count?: number }> = ({ cols, count = 6 }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <tr key={i} aria-hidden="true">
        {Array.from({ length: cols }, (_, j) => (
          <td key={j}>
            <SkLine w={j === 0 ? "70%" : "85%"} />
          </td>
        ))}
      </tr>
    ))}
  </>
);
const TilesSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className={styles.tiles}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className={`${styles.tile} ${styles.tileStatic}`}>
        <SkLine w="50%" size={12} />
        <SkLine w="70%" size={28} />
        <SkLine w="40%" size={12} />
      </div>
    ))}
  </div>
);
const CardSkeleton: React.FC = () => (
  <section className={styles.card}>
    <SkLine w="40%" />
    <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
      <SkLine />
      <SkLine w="90%" />
      <SkLine w="75%" />
      <SkLine w="85%" />
    </div>
  </section>
);

/* =====================================================================
   Small building blocks
   ===================================================================== */
const Bars: React.FC<{ items: { label: string; value: number; note?: string }[]; tone?: string; cur?: string }> = ({ items, tone = "fillBlue", cur }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className={styles.bars}>
      {items.map((i) => (
        <div key={i.label} className={styles.barRow}>
          <span className={styles.barLabel}>{i.label}</span>
          <span className={styles.barTrack}>
            <span className={`${styles.barFill} ${styles[tone]}`} style={{ width: `${(i.value / max) * 100}%` }} />
          </span>
          <span className={styles.barVal}>{i.note ?? money(i.value, cur)}</span>
        </div>
      ))}
      {items.length === 0 && <div className={styles.empty}>No sales yet.</div>}
    </div>
  );
};

const Columns: React.FC<{ items: { label: string; value: number }[]; unit: string; cur?: string }> = ({ items, unit, cur }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  if (items.every((i) => i.value === 0)) return <div className={styles.empty}>No sales in this period.</div>;
  return (
    <div className={styles.chart} role="img" aria-label={`${unit}: ${items.map((i) => `${i.label} ${money(i.value, cur)}`).join(", ")}`}>
      {items.map((i) => (
        <div key={i.label} className={styles.chartCol}>
          <div className={styles.chartBars}>
            <span className={styles.chartBar} style={{ height: i.value > 0 ? `${Math.max((i.value / max) * 100, 3)}%` : "0" }} title={`${i.label}: ${money(i.value, cur)}`} />
          </div>
          <span className={styles.chartLabel}>{i.label}</span>
        </div>
      ))}
    </div>
  );
};

const ErrorCard: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <section className={styles.card}>
    <div className={styles.formErr} role="alert">
      {message}
    </div>
    <Button size="small" onClick={onRetry}>
      Try again
    </Button>
  </section>
);

/* =====================================================================
   Act helpers
   ===================================================================== */
const patchAct = (route: string, label: string, icon: React.ReactNode, when: (r: Row) => boolean, patch: Record<string, any>, msg: (r: Row) => string): Act => ({
  label,
  icon,
  show: when,
  run: async (r) => {
    await crud<Row>(route).update(r.id, { ...patch, rowVersion: r.rowVersion });
    return msg(r);
  },
});
const activeActs = (route: string, name: (r: Row) => string): Act[] => [
  patchAct(route, "Deactivate", <Dismiss20Regular />, (r) => r.isActive !== false, { isActive: false }, (r) => `${name(r)} deactivated`),
  patchAct(route, "Activate", <Checkmark20Regular />, (r) => r.isActive === false, { isActive: true }, (r) => `${name(r)} activated`),
];
const activeCol: Col = { key: "isActive", label: "Status", kind: "status", get: (r) => (r.isActive === false ? "Inactive" : "Active") };

/* =====================================================================
   Sale detail (lines load when a sale is selected)
   ===================================================================== */
const SaleDetail: React.FC<{ row: Row }> = ({ row }) => {
  const { data, loading, error } = useLoad(() => saleApi.get(row.id), [row.id]);
  if (loading && !data)
    return (
      <div className={styles.lines}>
        <SkLine />
        <SkLine w="80%" />
        <SkLine w="60%" />
      </div>
    );
  if (error || !data) return <div className={styles.hint}>{error ?? "Could not load the lines."}</div>;
  return (
    <div className={styles.lines}>
      {data.lines
        .filter((l) => l.status === "Active")
        .map((l) => (
          <div key={l.id} className={styles.lineRow}>
            <span>
              {l.quantity} × {l.description}
            </span>
            <span>{money(l.lineTotal, data.currencyCode)}</span>
          </div>
        ))}
      {data.discountTotal > 0 && (
        <div className={styles.lineRow}>
          <span>Discount</span>
          <span>-{money(data.discountTotal, data.currencyCode)}</span>
        </div>
      )}
      <div className={styles.lineRow}>
        <span>Tax{data.taxInclusive ? " (included)" : ""}</span>
        <span>{money(data.taxTotal, data.currencyCode)}</span>
      </div>
      {data.payments.map((p) => (
        <div key={p.id} className={styles.lineRow}>
          <span>Paid by {p.tenderCode}</span>
          <span>{money(p.amount, data.currencyCode)}</span>
        </div>
      ))}
    </div>
  );
};

/* =====================================================================
   Pages (one config per list page; the engine below does the rest)
   ===================================================================== */
const ENTITIES: Entity[] = [
  { id: "dashboard", group: "Overview", title: "Overview", subtitle: "Today's trading at a glance.", noun: "", icon: <Home20Regular />, page: "dash", cols: [], fields: [], acts: [] },

  /* ---------------- Sales ---------------- */
  { id: "pos", group: "Sales", title: "Point of sale", subtitle: "Tap items, apply a coupon and take payment.", noun: "", icon: <Cart20Regular />, page: "pos", cols: [], fields: [], acts: [] },
  {
    id: "sales",
    group: "Sales",
    title: "Sales",
    subtitle: "Every till sale, with its lines and payments.",
    noun: "sale",
    icon: <Receipt20Regular />,
    page: "grid",
    route: "pos/sales",
    type: "Sale",
    readOnly: true,
    byStore: true,
    lookups: ["registers"],
    status: { key: "status", options: st("Open", "Suspended", "Completed", "Voided") },
    cols: [
      { key: "receiptNo", label: "Receipt", kind: "mono" },
      { key: "startedAtUtc", label: "Started", kind: "dt" },
      { key: "channel", label: "Channel" },
      { key: "register", label: "Register", kind: "lookup", lookup: "registers", get: (r) => r.registerId },
      { key: "subTotal", label: "Subtotal", kind: "money", right: true },
      { key: "discountTotal", label: "Discount", kind: "money", right: true },
      { key: "taxTotal", label: "Tax", kind: "money", right: true },
      { key: "total", label: "Total", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    Detail: SaleDetail,
    acts: [
      {
        label: "Void",
        icon: <Dismiss20Regular />,
        show: (r) => ["Open", "Suspended", "Completed"].includes(String(r.status)),
        ask: { label: "Reason for voiding", kind: "text" },
        run: async (r, _c, a) => {
          await saleApi.void(r.id, a?.trim() || "Voided from the back office");
          return `${r.receiptNo} voided`;
        },
      },
    ],
  },
  { id: "board", group: "Sales", title: "Order board", subtitle: "Online and pickup orders being prepared.", noun: "", icon: <Tv20Regular />, page: "board", cols: [], fields: [], acts: [] },
  {
    id: "orders",
    group: "Sales",
    title: "Online orders",
    subtitle: "Orders from the web store, app and marketplaces.",
    noun: "order",
    icon: <ShoppingBag20Regular />,
    page: "grid",
    route: "orders/orders",
    type: "CustomerOrder",
    readOnly: true,
    status: { key: "status", options: st("Pending", "Confirmed", "Sourced", "PartiallyFulfilled", "Fulfilled", "Completed", "Cancelled", "OnHold") },
    cols: [
      { key: "orderNo", label: "Order", kind: "mono" },
      { key: "placedAtUtc", label: "Placed", kind: "dt" },
      { key: "customerName", label: "Customer" },
      { key: "channelCode", label: "Channel" },
      { key: "preferredFulfillment", label: "Fulfilment" },
      { key: "total", label: "Total", kind: "money", right: true },
      { key: "paymentStatus", label: "Payment", kind: "status" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [
      {
        label: "Cancel order",
        icon: <Dismiss20Regular />,
        show: (r) => !["Completed", "Cancelled", "Fulfilled"].includes(String(r.status)),
        ask: { label: "Reason for cancelling", kind: "text" },
        run: async (r, _c, a) => {
          await orderApi.cancel(r.id, a?.trim() || undefined);
          return `${r.orderNo} cancelled`;
        },
      },
    ],
  },
  {
    id: "returns",
    group: "Sales",
    title: "Returns",
    subtitle: "Return authorisations from every channel.",
    noun: "return",
    icon: <ArrowUndo20Regular />,
    page: "grid",
    route: "returns/authorizations",
    type: "ReturnAuthorization",
    readOnly: true,
    status: { key: "status", options: st("Requested", "Authorized", "Received", "Inspected", "Refunded", "Rejected", "Cancelled", "Closed") },
    cols: [
      { key: "rmaNo", label: "Return", kind: "mono" },
      { key: "requestedAtUtc", label: "Requested", kind: "dt" },
      { key: "channelCode", label: "Channel" },
      { key: "refundMethod", label: "Refund to" },
      { key: "refundTotal", label: "Refund", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },

  /* ---------------- Catalog ---------------- */
  {
    id: "products",
    group: "Catalog",
    title: "Products",
    subtitle: "Items, prices and costs.",
    noun: "product",
    icon: <Food20Regular />,
    page: "grid",
    route: "catalog/products",
    type: "Product",
    lookups: ["categories"],
    status: { key: "status", options: st("Active", "Draft", "Discontinued", "Blocked") },
    defaults: (c) => ({ status: "Active", baseCurrency: c.currency, costCurrency: c.currency }),
    cols: [
      { key: "sku", label: "SKU", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category", kind: "lookup", lookup: "categories", get: (r) => r.categoryId },
      { key: "basePrice", label: "Price", kind: "money", right: true },
      { key: "standardCost", label: "Cost", kind: "money", right: true },
      { key: "margin", label: "Margin", kind: "pct", right: true, get: (r) => (num(r.basePrice) ? Math.round(((num(r.basePrice) - num(r.standardCost)) / num(r.basePrice)) * 100) : 0) },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "sku", label: "SKU", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "categoryId", label: "Category", kind: "select", lookup: "categories" },
      { key: "unitOfMeasure", label: "Unit", kind: "text" },
      { key: "basePrice", label: "Selling price", kind: "number", required: true },
      { key: "standardCost", label: "Cost", kind: "number", required: true },
      { key: "description", label: "Description", kind: "text", full: true },
    ],
    validate: positive("basePrice", "Price"),
    acts: [
      patchAct("catalog/products", "Discontinue", <Dismiss20Regular />, (r) => r.status === "Active", { status: "Discontinued" }, (r) => `${r.name} discontinued`),
      patchAct("catalog/products", "Activate", <Checkmark20Regular />, (r) => r.status !== "Active", { status: "Active" }, (r) => `${r.name} activated`),
    ],
  },
  {
    id: "categories",
    group: "Catalog",
    title: "Categories",
    subtitle: "Groups shown as tabs on the point of sale.",
    noun: "category",
    icon: <TagMultiple20Regular />,
    page: "grid",
    route: "catalog/categories",
    type: "Category",
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "categoryRole", label: "Role" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
    ],
    acts: activeActs("catalog/categories", (r) => String(r.name)),
  },
  {
    id: "promotions",
    group: "Catalog",
    title: "Promotions",
    subtitle: "Automatic discounts the pricing engine applies to a basket.",
    noun: "promotion",
    icon: <Tag20Regular />,
    page: "grid",
    route: "pricing/promotions",
    type: "Promotion",
    status: { key: "isActive", options: ACTIVE_OPTS },
    defaults: (c) => ({ approvalStatus: "Approved", isActive: true, currencyCode: c.currency }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "value", label: "Value", right: true, get: (r) => (num(r.discountPercent) > 0 ? `${num(r.discountPercent)}%` : money(num(r.discountAmount))) },
      { key: "validFromUtc", label: "From", kind: "date" },
      { key: "validToUtc", label: "To", kind: "date" },
      { key: "approvalStatus", label: "Approval", kind: "status" },
      activeCol,
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: st("PercentOff", "AmountOff", "SpendThreshold"), required: true },
      { key: "discountPercent", label: "Percent off", kind: "number" },
      { key: "discountAmount", label: "Amount off", kind: "number" },
      { key: "minSubtotal", label: "Minimum spend", kind: "number" },
      { key: "validFromUtc", label: "Starts", kind: "datetime", required: true },
      { key: "validToUtc", label: "Ends", kind: "datetime" },
    ],
    validate: (v) => {
      if (v.type === "PercentOff" && !(num(v.discountPercent) > 0 && num(v.discountPercent) <= 100)) return "Percent off must be between 1 and 100.";
      if ((v.type === "AmountOff" || v.type === "SpendThreshold") && !(num(v.discountAmount) > 0 || num(v.discountPercent) > 0)) return "Enter an amount off or a percent off.";
      return null;
    },
    acts: activeActs("pricing/promotions", (r) => String(r.name)),
  },
  {
    id: "coupons",
    group: "Catalog",
    title: "Coupons",
    subtitle: "Codes that unlock a promotion.",
    noun: "coupon",
    icon: <TagMultiple20Regular />,
    page: "grid",
    route: "pricing/coupons",
    type: "Coupon",
    lookups: ["promotions"],
    status: { key: "isActive", options: ACTIVE_OPTS },
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "promotion", label: "Promotion", kind: "lookup", lookup: "promotions", get: (r) => r.promotionId },
      { key: "redemptionCount", label: "Used", kind: "num", right: true },
      { key: "maxRedemptions", label: "Limit", kind: "num", right: true },
      { key: "expiresAtUtc", label: "Expires", kind: "date" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "promotionId", label: "Promotion", kind: "select", lookup: "promotions", required: true },
      { key: "maxRedemptions", label: "Redemption limit", kind: "number" },
      { key: "expiresAtUtc", label: "Expires", kind: "datetime" },
    ],
    acts: activeActs("pricing/coupons", (r) => String(r.code)),
  },
  {
    id: "pricelists",
    group: "Catalog",
    title: "Price lists",
    subtitle: "Prices by channel, store and customer segment.",
    noun: "price list",
    icon: <Money20Regular />,
    page: "grid",
    route: "pricing/price-lists",
    type: "PriceList",
    readOnly: true,
    status: { key: "status", options: st("Draft", "PendingApproval", "Approved", "Rejected", "Expired") },
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "currencyCode", label: "Currency" },
      { key: "channel", label: "Channel" },
      { key: "priority", label: "Priority", kind: "num", right: true },
      { key: "validFromUtc", label: "From", kind: "date" },
      { key: "validToUtc", label: "To", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },
  {
    id: "markdowns",
    group: "Catalog",
    title: "Markdowns",
    subtitle: "Clearance and expiry price cuts.",
    noun: "markdown",
    icon: <Tag20Regular />,
    page: "grid",
    route: "pricing/markdown-plans",
    type: "MarkdownPlan",
    readOnly: true,
    status: { key: "status", options: st("Draft", "PendingApproval", "Approved", "Active", "Completed", "Rejected") },
    cols: [
      { key: "name", label: "Name" },
      { key: "reason", label: "Reason" },
      { key: "percent", label: "Cut", kind: "pct", right: true },
      { key: "startsAtUtc", label: "Starts", kind: "dt" },
      { key: "endsAtUtc", label: "Ends", kind: "dt" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },

  /* ---------------- Inventory ---------------- */
  {
    id: "stock",
    group: "Inventory",
    title: "Stock levels",
    subtitle: "On-hand and reserved quantities by location.",
    noun: "stock line",
    icon: <Box20Regular />,
    page: "grid",
    route: "inventory/stock",
    type: "StockOnHand",
    readOnly: true,
    byStore: true,
    lookups: ["products"],
    cols: [
      { key: "product", label: "Product", kind: "lookup", lookup: "products", get: (r) => r.productId },
      { key: "qtyOnHand", label: "On hand", kind: "num", right: true },
      { key: "qtyReserved", label: "Reserved", kind: "num", right: true },
      { key: "available", label: "Available", kind: "num", right: true, get: (r) => num(r.qtyOnHand) - num(r.qtyReserved) },
      { key: "averageCost", label: "Average cost", kind: "money", right: true },
      { key: "lastMovementAtUtc", label: "Last movement", kind: "dt" },
    ],
    fields: [],
    acts: [],
  },
  {
    id: "movements",
    group: "Inventory",
    title: "Stock movements",
    subtitle: "Every change to stock, newest first.",
    noun: "movement",
    icon: <History20Regular />,
    page: "grid",
    route: "inventory/movements",
    type: "StockMovement",
    readOnly: true,
    byStore: true,
    lookups: ["products"],
    cols: [
      { key: "occurredAtUtc", label: "When", kind: "dt" },
      { key: "type", label: "Type" },
      { key: "product", label: "Product", kind: "lookup", lookup: "products", get: (r) => r.productId },
      { key: "quantity", label: "Quantity", kind: "num", right: true },
      { key: "balanceAfter", label: "Balance", kind: "num", right: true },
      { key: "referenceType", label: "Source" },
      { key: "reason", label: "Reason" },
    ],
    fields: [],
    acts: [],
  },
  {
    id: "transfers",
    group: "Inventory",
    title: "Transfers",
    subtitle: "Stock moving between stores and distribution centres.",
    noun: "transfer",
    icon: <ArrowSwap20Regular />,
    page: "grid",
    route: "inventory/transfers",
    type: "StockTransfer",
    readOnly: true,
    lookups: ["stores"],
    status: { key: "status", options: st("Draft", "Approved", "Shipped", "PartiallyReceived", "Received", "Cancelled") },
    cols: [
      { key: "transferNo", label: "Transfer", kind: "mono" },
      { key: "from", label: "From", kind: "lookup", lookup: "stores", get: (r) => r.fromStoreId },
      { key: "to", label: "To", kind: "lookup", lookup: "stores", get: (r) => r.toStoreId },
      { key: "reason", label: "Reason" },
      { key: "shippedAtUtc", label: "Shipped", kind: "dt" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },
  {
    id: "purchases",
    group: "Inventory",
    title: "Purchase orders",
    subtitle: "Orders placed with suppliers.",
    noun: "purchase order",
    icon: <Cart20Regular />,
    page: "grid",
    route: "procurement/purchase-orders",
    type: "PurchaseOrder",
    readOnly: true,
    lookups: ["suppliers"],
    status: { key: "status", options: st("Draft", "PendingApproval", "Approved", "Sent", "PartiallyReceived", "Received", "Closed", "Cancelled") },
    cols: [
      { key: "poNo", label: "Purchase order", kind: "mono" },
      { key: "supplier", label: "Supplier", kind: "lookup", lookup: "suppliers", get: (r) => r.supplierId },
      { key: "orderedAtUtc", label: "Ordered", kind: "dt" },
      { key: "expectedDate", label: "Expected", kind: "date" },
      { key: "total", label: "Total", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },
  {
    id: "suppliers",
    group: "Inventory",
    title: "Suppliers",
    subtitle: "Who you buy from.",
    noun: "supplier",
    icon: <Building20Regular />,
    page: "grid",
    route: "procurement/suppliers",
    type: "Supplier",
    status: { key: "isActive", options: ACTIVE_OPTS },
    defaults: (c) => ({ currencyCode: c.currency, countryCode: "NG", isActive: true }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "paymentTerms", label: "Payment terms" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "email", label: "Email", kind: "text" },
      { key: "paymentTerms", label: "Payment terms", kind: "select", options: st("COD", "NET7", "NET15", "NET30"), required: true },
    ],
    acts: activeActs("procurement/suppliers", (r) => String(r.name)),
  },
  {
    id: "wastage",
    group: "Inventory",
    title: "Wastage",
    subtitle: "Spoiled and expired stock written off.",
    noun: "wastage entry",
    icon: <Delete20Regular />,
    page: "grid",
    route: "fresh/waste",
    type: "WasteRecord",
    readOnly: true,
    byStore: true,
    lookups: ["products"],
    cols: [
      { key: "businessDate", label: "Date", kind: "date" },
      { key: "product", label: "Product", kind: "lookup", lookup: "products", get: (r) => r.productId },
      { key: "quantity", label: "Quantity", kind: "num", right: true },
      { key: "costValue", label: "Cost of loss", kind: "money", right: true },
      { key: "reason", label: "Reason" },
    ],
    fields: [],
    acts: [],
  },

  /* ---------------- Customers ---------------- */
  {
    id: "customers",
    group: "Customers",
    title: "Customers",
    subtitle: "Guests and their purchase history.",
    noun: "customer",
    icon: <People20Regular />,
    page: "grid",
    route: "customers/customers",
    type: "Customer",
    defaults: () => ({ segment: "New", preferredCulture: "en" }),
    cols: [
      { key: "customerNo", label: "Customer", kind: "mono" },
      { key: "displayName", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "segment", label: "Segment" },
      { key: "lifetimeValue", label: "Lifetime value", kind: "money", right: true },
      { key: "orderCount", label: "Orders", kind: "num", right: true },
    ],
    fields: [
      { key: "customerNo", label: "Customer no.", kind: "text", required: true },
      { key: "displayName", label: "Name", kind: "text", required: true, full: true },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "email", label: "Email", kind: "text" },
    ],
    acts: [],
  },
  {
    id: "loyalty",
    group: "Customers",
    title: "Loyalty accounts",
    subtitle: "Points balances and tiers.",
    noun: "loyalty account",
    icon: <Person20Regular />,
    page: "grid",
    route: "customers/loyalty-accounts",
    type: "LoyaltyAccount",
    readOnly: true,
    lookups: ["customers"],
    cols: [
      { key: "cardNumber", label: "Card", kind: "mono" },
      { key: "customer", label: "Customer", kind: "lookup", lookup: "customers", get: (r) => r.customerId },
      { key: "pointsBalance", label: "Points", kind: "num", right: true },
      { key: "lifetimePoints", label: "Lifetime points", kind: "num", right: true },
      { key: "enrolledAtUtc", label: "Enrolled", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },
  {
    id: "giftcards",
    group: "Customers",
    title: "Gift cards",
    subtitle: "Gift cards, store credit and vouchers.",
    noun: "card",
    icon: <Gift20Regular />,
    page: "grid",
    route: "storedvalue/accounts",
    type: "StoredValueAccount",
    readOnly: true,
    status: { key: "status", options: st("Inactive", "Active", "Blocked", "Expired", "Redeemed", "Cancelled") },
    cols: [
      { key: "number", label: "Number", kind: "mono" },
      { key: "kind", label: "Type" },
      { key: "balance", label: "Balance", kind: "money", right: true },
      { key: "currencyCode", label: "Currency" },
      { key: "expiresAtUtc", label: "Expires", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [],
  },

  /* ---------------- Staff ---------------- */
  {
    id: "associates",
    group: "Staff",
    title: "Staff",
    subtitle: "Cashiers and store associates. Logins live in the central auth service.",
    noun: "staff member",
    icon: <Person20Regular />,
    page: "grid",
    route: "organization/associates",
    type: "Associate",
    lookups: ["stores"],
    status: { key: "isActive", options: ACTIVE_OPTS },
    defaults: (c) => ({ currencyCode: c.currency, isActive: true }),
    cols: [
      { key: "employeeNo", label: "Employee", kind: "mono" },
      { key: "displayName", label: "Name" },
      { key: "title", label: "Title" },
      { key: "store", label: "Home store", kind: "lookup", lookup: "stores", get: (r) => r.homeStoreId },
      { key: "phone", label: "Phone" },
      activeCol,
    ],
    fields: [
      { key: "userId", label: "Central auth user id", kind: "text", required: true, full: true },
      { key: "employeeNo", label: "Employee no.", kind: "text", required: true },
      { key: "displayName", label: "Name", kind: "text", required: true },
      { key: "title", label: "Title", kind: "text" },
      { key: "homeStoreId", label: "Home store", kind: "select", lookup: "stores" },
      { key: "phone", label: "Phone", kind: "text" },
    ],
    acts: activeActs("organization/associates", (r) => String(r.displayName)),
  },
  {
    id: "tills",
    group: "Staff",
    title: "Tills and cash drawer",
    subtitle: "Open a till before taking payments. Close it to reconcile cash.",
    noun: "till",
    icon: <Clock20Regular />,
    page: "grid",
    route: "cash/till-sessions",
    type: "TillSession",
    readOnly: true,
    byStore: true,
    lookups: ["registers"],
    status: { key: "status", options: st("Open", "Closed", "Reconciled") },
    cols: [
      { key: "businessDate", label: "Date", kind: "date" },
      { key: "register", label: "Register", kind: "lookup", lookup: "registers", get: (r) => r.registerId },
      { key: "openedAtUtc", label: "Opened", kind: "dt" },
      { key: "openingFloat", label: "Opening float", kind: "money", right: true },
      { key: "expectedCash", label: "Expected cash", kind: "money", right: true },
      { key: "countedCash", label: "Counted", kind: "money", right: true },
      { key: "variance", label: "Variance", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [
      {
        label: "Close till",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Open",
        ask: { label: "Cash counted in the drawer", kind: "number" },
        run: async (r, _c, a) => {
          const counted = Number(a);
          if (!(counted >= 0) || a === undefined || a.trim() === "") throw new Error("Enter the cash counted.");
          await cashApi.closeTill(r.id, [{ tenderCode: "CASH", currencyCode: String(r.currencyCode), countedAmount: counted }]);
          return "Till closed";
        },
      },
    ],
  },

  /* ---------------- Reports ---------------- */
  { id: "reports", group: "Reports", title: "Sales reports", subtitle: "Sales by day, category, payment method and cashier.", noun: "", icon: <DataBarVertical20Regular />, page: "reports", cols: [], fields: [], acts: [] },

  /* ---------------- Setup ---------------- */
  {
    id: "stores",
    group: "Setup",
    title: "Stores",
    subtitle: "Stores, warehouses and distribution centres.",
    noun: "store",
    icon: <Building20Regular />,
    page: "grid",
    route: "organization/stores",
    type: "Store",
    status: { key: "isActive", options: ACTIVE_OPTS },
    defaults: (c) => ({ type: "Store", countryCode: "NG", currencyCode: c.currency, timeZoneId: "Africa/Lagos", cultureCode: "en", isActive: true }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "city", label: "City" },
      { key: "currencyCode", label: "Currency" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: st("Store", "DistributionCenter", "Warehouse", "DarkStore"), required: true },
      { key: "city", label: "City", kind: "text" },
      { key: "countryCode", label: "Country code", kind: "text", required: true },
      { key: "currencyCode", label: "Currency", kind: "text", required: true },
      { key: "timeZoneId", label: "Time zone", kind: "text", required: true },
    ],
    acts: activeActs("organization/stores", (r) => String(r.name)),
  },
  {
    id: "registers",
    group: "Setup",
    title: "Registers",
    subtitle: "Tills, mobile devices, self-checkouts and kiosks.",
    noun: "register",
    icon: <Desktop20Regular />,
    page: "grid",
    route: "organization/registers",
    type: "Register",
    lookups: ["stores"],
    status: { key: "status", options: st("Active", "Offline", "Maintenance", "Retired") },
    defaults: () => ({ status: "Active" }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "store", label: "Store", kind: "lookup", lookup: "stores", get: (r) => r.storeId },
      { key: "type", label: "Type" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "storeId", label: "Store", kind: "select", lookup: "stores", required: true, full: true },
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: st("Pos", "MobilePos", "SelfCheckout", "Kiosk"), required: true },
    ],
    acts: [
      patchAct("organization/registers", "Retire", <Dismiss20Regular />, (r) => r.status !== "Retired", { status: "Retired" }, (r) => `${r.name} retired`),
      patchAct("organization/registers", "Activate", <Checkmark20Regular />, (r) => r.status !== "Active", { status: "Active" }, (r) => `${r.name} activated`),
    ],
  },
  {
    id: "tenders",
    group: "Setup",
    title: "Payment methods",
    subtitle: "Tender types the till accepts.",
    noun: "payment method",
    icon: <Payment20Regular />,
    page: "grid",
    route: "payments/tender-types",
    type: "TenderType",
    status: { key: "isActive", options: ACTIVE_OPTS },
    defaults: () => ({ isActive: true, glKey: "Tender.Clearing" }),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "kind", label: "Type" },
      { key: "glKey", label: "Ledger key", kind: "mono" },
      activeCol,
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "kind", label: "Type", kind: "select", options: st("Cash", "Card", "Wallet", "GiftCard", "LoyaltyPoints", "Bnpl", "Cheque", "Voucher", "StoreCredit", "BankTransfer", "MobileMoney"), required: true },
      { key: "glKey", label: "Ledger key", kind: "text", required: true },
    ],
    acts: activeActs("payments/tender-types", (r) => String(r.name)),
  },
  {
    id: "taxrates",
    group: "Setup",
    title: "Tax rates",
    subtitle: "Rates by jurisdiction and product tax category.",
    noun: "tax rate",
    icon: <Calculator20Regular />,
    page: "grid",
    route: "tax/rates",
    type: "TaxRate",
    lookups: ["jurisdictions", "taxCategories"],
    defaults: () => ({ glKey: "Tax.Payable" }),
    cols: [
      { key: "name", label: "Name" },
      { key: "jurisdiction", label: "Jurisdiction", kind: "lookup", lookup: "jurisdictions", get: (r) => r.jurisdictionId },
      { key: "category", label: "Tax category", kind: "lookup", lookup: "taxCategories", get: (r) => r.taxCategoryId },
      { key: "rate", label: "Rate", kind: "pct", right: true, get: (r) => Math.round(num(r.rate) * 10000) / 100 },
      { key: "validFromUtc", label: "From", kind: "date" },
      { key: "validToUtc", label: "To", kind: "date" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "rate", label: "Rate (%)", kind: "number", required: true, pct: true },
      { key: "jurisdictionId", label: "Jurisdiction", kind: "select", lookup: "jurisdictions", required: true },
      { key: "taxCategoryId", label: "Tax category", kind: "select", lookup: "taxCategories", required: true },
      { key: "validFromUtc", label: "Starts", kind: "datetime", required: true },
      { key: "validToUtc", label: "Ends", kind: "datetime" },
    ],
    acts: [],
  },
  {
    id: "fx",
    group: "Setup",
    title: "Exchange rate overrides",
    subtitle: "Your own rate wins over the market rate while it is valid.",
    noun: "rate override",
    icon: <Globe20Regular />,
    page: "grid",
    route: "admin/fx-overrides",
    type: "TenantRateOverride",
    cols: [
      { key: "fromCurrency", label: "From" },
      { key: "toCurrency", label: "To" },
      { key: "rate", label: "Rate", kind: "num", right: true },
      { key: "validFromUtc", label: "Valid from", kind: "date" },
      { key: "validToUtc", label: "Valid to", kind: "date" },
      { key: "reason", label: "Reason" },
    ],
    fields: [
      { key: "fromCurrency", label: "From currency", kind: "text", required: true },
      { key: "toCurrency", label: "To currency", kind: "text", required: true },
      { key: "rate", label: "Rate", kind: "number", required: true },
      { key: "validFromUtc", label: "Valid from", kind: "datetime", required: true },
      { key: "validToUtc", label: "Valid to", kind: "datetime" },
      { key: "reason", label: "Reason", kind: "text", full: true },
    ],
    validate: positive("rate", "Rate"),
    acts: [],
  },
  {
    id: "translations",
    group: "Setup",
    title: "Translations",
    subtitle: "Override any message or label for a language.",
    noun: "translation",
    icon: <Globe20Regular />,
    page: "grid",
    route: "admin/translations",
    type: "Translation",
    cols: [
      { key: "culture", label: "Language", kind: "mono" },
      { key: "key", label: "Key", kind: "mono" },
      { key: "value", label: "Text" },
    ],
    fields: [
      { key: "culture", label: "Language", kind: "text", required: true },
      { key: "key", label: "Key", kind: "text", required: true },
      { key: "value", label: "Text", kind: "text", required: true, full: true },
    ],
    acts: [],
  },

  /* ---------------- Integration ---------------- */
  {
    id: "glmappings",
    group: "Integration",
    title: "Ledger accounts",
    subtitle: "Which accounting account each retail posting key uses.",
    noun: "mapping",
    icon: <Link20Regular />,
    page: "grid",
    route: "integration/gl-mappings",
    type: "GlAccountMapping",
    cols: [
      { key: "key", label: "Posting key", kind: "mono" },
      { key: "accountCode", label: "Account code", kind: "mono" },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "key", label: "Posting key", kind: "text", required: true },
      { key: "accountCode", label: "Account code", kind: "text", required: true },
      { key: "description", label: "Description", kind: "text", full: true },
    ],
    acts: [],
  },
  {
    id: "postings",
    group: "Integration",
    title: "Accounting postings",
    subtitle: "Journals sent to the accounting system.",
    noun: "posting",
    icon: <Receipt20Regular />,
    page: "grid",
    route: "integration/postings",
    type: "AccountingPosting",
    readOnly: true,
    status: { key: "status", options: st("Pending", "Posted", "Failed", "Skipped") },
    cols: [
      { key: "businessDate", label: "Date", kind: "date" },
      { key: "sourceType", label: "Source" },
      { key: "totalDebit", label: "Debit", kind: "money", right: true },
      { key: "totalCredit", label: "Credit", kind: "money", right: true },
      { key: "attempts", label: "Attempts", kind: "num", right: true },
      { key: "status", label: "Status", kind: "status" },
      { key: "error", label: "Error" },
    ],
    fields: [],
    acts: [],
  },

  /* ---------------- System ---------------- */
  {
    id: "alerts",
    group: "System",
    title: "Loss prevention alerts",
    subtitle: "Unusual voids, refunds and discounts to look into.",
    noun: "alert",
    icon: <Warning20Regular />,
    page: "grid",
    route: "lp/alerts",
    type: "LpAlert",
    readOnly: true,
    byStore: true,
    lookups: ["stores"],
    status: { key: "status", options: st("Open", "Investigating", "Closed", "FalsePositive") },
    cols: [
      { key: "createdAtUtc", label: "Raised", kind: "dt" },
      { key: "title", label: "Alert" },
      { key: "store", label: "Store", kind: "lookup", lookup: "stores", get: (r) => r.storeId },
      { key: "severity", label: "Severity", kind: "status" },
      { key: "riskScore", label: "Risk", kind: "num", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    acts: [
      patchAct("lp/alerts", "Investigate", <Edit20Regular />, (r) => r.status === "Open", { status: "Investigating" }, () => "Alert is being investigated"),
      patchAct("lp/alerts", "Close", <Checkmark20Regular />, (r) => r.status === "Open" || r.status === "Investigating", { status: "Closed" }, () => "Alert closed"),
      patchAct("lp/alerts", "False positive", <Dismiss20Regular />, (r) => r.status === "Open" || r.status === "Investigating", { status: "FalsePositive" }, () => "Marked as a false positive"),
    ],
  },
];

const GROUP_ORDER = ["Overview", "Sales", "Catalog", "Inventory", "Customers", "Staff", "Reports", "Setup", "Integration", "System"];

/* =====================================================================
   Record form (New / Edit)
   ===================================================================== */
const RecordDialog: React.FC<{
  ent: Entity;
  lk: LkMap;
  row: Row | null;
  onClose: () => void;
  onSave: (v: Record<string, any>) => Promise<string | null>;
}> = ({ ent, lk, row, onClose, onSave }) => {
  const ctx = useCtx();
  const [v, setV] = useState<Record<string, string>>(() => {
    const dflt = row ? {} : ent.defaults?.(ctx) ?? {};
    const o: Record<string, string> = {};
    ent.fields.forEach((f) => {
      let cur: any = row ? row[f.key] : dflt[f.key];
      if (cur !== undefined && cur !== null) {
        if (f.pct) cur = Math.round(num(cur) * 10000) / 100;
        if (f.kind === "date" || f.kind === "datetime") cur = String(cur).slice(0, 10);
        o[f.key] = String(cur);
      } else o[f.key] = !row && (f.kind === "date" || f.kind === "datetime") ? off(0) : "";
    });
    return o;
  });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const missing = ent.fields.find((f) => f.required && !(v[f.key] ?? "").trim());
    if (missing) return setErr(`${missing.label} is required.`);
    const out: Record<string, any> = {};
    ent.fields.forEach((f) => {
      const raw = (v[f.key] ?? "").trim();
      if (f.kind === "number") out[f.key] = f.pct ? (Number(raw) || 0) / 100 : Number(raw) || 0;
      else if (f.kind === "datetime") {
        if (raw) out[f.key] = `${raw}T00:00:00Z`;
      } else if (f.kind === "date" || f.kind === "select") {
        if (raw) out[f.key] = raw;
      } else out[f.key] = raw;
    });
    setSaving(true);
    const bad = await onSave(out);
    setSaving(false);
    if (bad) setErr(bad);
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {row ? `Edit ${ent.noun}` : `New ${ent.noun}`}
          </DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              {ent.fields.map((f) => {
                const options: Opt[] = f.options ?? (f.lookup ? Array.from(lk[f.lookup] ?? []).map(([val2, label]) => ({ v: val2, l: label })) : []);
                return (
                  <div key={f.key} className={f.full ? styles.formFull : undefined}>
                    <Field label={f.label} required={!!f.required}>
                      {f.kind === "select" ? (
                        <Select value={v[f.key] ?? ""} onChange={(_, d) => setV({ ...v, [f.key]: d.value })}>
                          <option value="">Select…</option>
                          {options.map((o) => (
                            <option key={o.v} value={o.v}>
                              {o.l}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input type={f.kind === "number" ? "number" : f.kind === "date" || f.kind === "datetime" ? "date" : "text"} value={v[f.key] ?? ""} onChange={(_, d) => setV({ ...v, [f.key]: d.value })} />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && (
              <div className={styles.formErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button size="small" className={styles.primary} appearance="primary" disabled={saving} onClick={() => void submit()}>
              {saving ? "Saving…" : row ? "Save" : "Create"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const AskDialog: React.FC<{ act: Act; onCancel: () => void; onOk: (answer: string) => void }> = ({ act, onCancel, onOk }) => {
  const [value, setValue] = useState("");
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onCancel()}>
      <DialogSurface className={styles.dialogSm}>
        <DialogBody>
          <DialogTitle>{act.label}</DialogTitle>
          <DialogContent>
            <Field label={act.ask?.label}>
              <Input type={act.ask?.kind === "number" ? "number" : "text"} value={value} onChange={(_, d) => setValue(d.value)} />
            </Field>
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button size="small" className={styles.primary} appearance="primary" onClick={() => onOk(value)}>
              {act.label}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* =====================================================================
   List page (every CRUD page)
   ===================================================================== */
const EntityPage: React.FC<{ ent: Entity }> = ({ ent }) => {
  const ctx = useCtx();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [page, setPage] = useState(1);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [ask, setAsk] = useState<{ act: Act; row: Row } | null>(null);
  const [busy, setBusy] = useState(false);

  const lk = useLookups(ent.lookups ?? []);
  const store = useMemo(() => crud<Row>(ent.route as string), [ent.route]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDq(q.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [q]);

  const storeId = ctx.device?.storeId;
  const query = useMemo<Query>(() => {
    const o: Query = { page, pageSize: PAGE_SIZE };
    if (dq) o.search = dq;
    if (sort) o.sort = sort.dir === -1 ? `-${sort.key}` : sort.key;
    if (ent.status && statusF) o[ent.status.key] = statusF;
    if (ent.byStore && storeId) o.storeId = storeId;
    return o;
  }, [page, dq, sort, statusF, ent.status, ent.byStore, storeId]);

  const { data, loading, error, reload } = useLoad(() => store.list(query), [store, JSON.stringify(query)]);
  useLive((e) => !!ent.type && e.entityType === ent.type, reload);

  const list = data?.items ?? [];
  const total = data?.totalCount ?? 0;
  const pages = Math.max(1, data?.totalPages ?? 1);
  const sel = list.find((r) => r.id === selId) ?? null;
  const first = loading && !data;

  const execute = async (a: Act, row: Row, answer?: string) => {
    setBusy(true);
    try {
      ctx.say(await a.run(row, ctx, answer));
      reload();
    } catch (e) {
      ctx.say(errorMessage(e), true);
    } finally {
      setBusy(false);
    }
  };
  const runAct = (a: Act) => {
    if (!sel) return;
    if (a.ask) setAsk({ act: a, row: sel });
    else void execute(a, sel);
  };

  const save = async (values: Record<string, any>): Promise<string | null> => {
    const bad = ent.validate ? ent.validate(values) : null;
    if (bad) return bad;
    try {
      if (form === "edit" && sel) {
        await store.update(sel.id, { ...values, rowVersion: sel.rowVersion });
        ctx.say(`${cap(ent.noun)} saved`);
      } else {
        const created = await store.create({ ...(ent.defaults?.(ctx) ?? {}), ...values });
        setSelId(created.id);
        ctx.say(`${cap(ent.noun)} created`);
      }
      setForm(null);
      reload();
      return null;
    } catch (e) {
      return errorMessage(e);
    }
  };

  const remove = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await store.remove(sel.id);
      ctx.say(`${cap(ent.noun)} deleted`);
      setSelId(null);
      setConfirmDel(false);
      reload();
    } catch (e) {
      ctx.say(errorMessage(e), true);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const lines = [ent.cols.map((c) => esc(c.label)).join(",")].concat(list.map((r) => ent.cols.map((c) => esc(show(c, val(c, r, lk), lk))).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ent.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortBy = (key: string) => {
    setPage(1);
    setSort((s) => (s && s.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }));
  };

  return (
    <>
      <div className={styles.pane} role="toolbar" aria-label="Actions">
        {!ent.readOnly && (
          <>
            <Button size="small" className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setForm("new")}>
              New
            </Button>
            <Button size="small" appearance="subtle" icon={<Edit20Regular />} disabled={!sel || busy} onClick={() => setForm("edit")}>
              Edit
            </Button>
            <Button size="small" appearance="subtle" icon={<Delete20Regular />} disabled={!sel || busy} onClick={() => setConfirmDel(true)}>
              Delete
            </Button>
          </>
        )}
        {!ent.readOnly && ent.acts.length > 0 && <span className={styles.sep} />}
        {ent.acts.map((a) => (
          <Button size="small" key={a.label} appearance="subtle" icon={a.icon as React.ReactElement} disabled={!sel || !a.show(sel) || busy} onClick={() => runAct(a)}>
            {a.label}
          </Button>
        ))}
        <span className={styles.grow} />
        <Button size="small" appearance="subtle" icon={<ArrowDownload20Regular />} disabled={list.length === 0} onClick={exportCsv}>
          Export page
        </Button>
      </div>

      <div className={styles.filters}>
        <SearchBox className={styles.search} placeholder={`Search ${ent.title.toLowerCase()}`} value={q} onChange={(_, d) => setQ(d.value)} />
        {ent.status && (
          <Select
            className={styles.statusSel}
            value={statusF}
            onChange={(_, d) => {
              setStatusF(d.value);
              setPage(1);
            }}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {ent.status.options.map((o) => (
              <option key={o.v} value={o.v}>
                {o.l}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div className={styles.work}>
        <div className={styles.gridWrap}>
          <table className={styles.grid}>
            <thead>
              <tr>
                {ent.cols.map((c) => {
                  const sortable = !c.get;
                  return (
                    <th key={c.key} className={c.right ? styles.right : undefined} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                      <button type="button" className={styles.th} onClick={() => sortable && sortBy(c.key)} style={sortable ? undefined : { cursor: "default" }}>
                        {c.label}
                        {sort?.key === c.key && (sort.dir === 1 ? <ChevronUp12Regular /> : <ChevronDown12Regular />)}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {first && <TableSkeleton cols={ent.cols.length} />}
              {!first &&
                list.map((r) => (
                  <tr
                    key={r.id}
                    className={r.id === selId ? styles.rowSel : undefined}
                    aria-selected={r.id === selId}
                    tabIndex={0}
                    onClick={() => setSelId(r.id)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSelId(r.id))}
                  >
                    {ent.cols.map((c) => {
                      const v = val(c, r, lk);
                      return (
                        <td key={c.key} className={`${c.right ? styles.right : ""} ${c.kind === "mono" ? styles.mono : ""}`}>
                          {c.kind === "status" ? v ? <Pill text={String(v)} /> : null : show(c, v, lk)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              {!first && list.length === 0 && (
                <tr>
                  <td colSpan={ent.cols.length} className={styles.empty}>
                    {error ?? "Nothing here yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {sel && (
          <aside className={styles.detail} aria-label="Details">
            <div className={styles.detailHead}>
              <strong>{String(val(ent.cols[0] as Col, sel, lk) ?? short(sel.id))}</strong>
              <Button appearance="subtle" size="small" icon={<Dismiss20Regular />} aria-label="Close details" onClick={() => setSelId(null)} />
            </div>
            <dl className={styles.dl}>
              {ent.cols.slice(1).map((c) => {
                const v = val(c, sel, lk);
                return (
                  <div key={c.key} className={styles.dlRow}>
                    <dt>{c.label}</dt>
                    <dd>{c.kind === "status" ? v ? <Pill text={String(v)} /> : "—" : show(c, v, lk) || "—"}</dd>
                  </div>
                );
              })}
            </dl>
            {ent.Detail && <ent.Detail row={sel} />}
          </aside>
        )}
      </div>

      <div className={styles.foot}>
        <span>{first ? "Loading…" : `${total} ${total === 1 ? "record" : "records"}`}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Button size="small" appearance="subtle" icon={<ChevronLeft20Regular />} aria-label="Previous page" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} />
          Page {page} of {pages}
          <Button size="small" appearance="subtle" icon={<ChevronRight20Regular />} aria-label="Next page" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)} />
        </span>
      </div>

      {form && <RecordDialog key={`${form}${sel?.id ?? ""}`} ent={ent} lk={lk} row={form === "edit" ? sel : null} onClose={() => setForm(null)} onSave={save} />}
      {ask && (
        <AskDialog
          act={ask.act}
          onCancel={() => setAsk(null)}
          onOk={(answer) => {
            const { act, row } = ask;
            setAsk(null);
            void execute(act, row, answer);
          }}
        />
      )}

      <Dialog open={confirmDel} onOpenChange={(_, d) => !d.open && setConfirmDel(false)}>
        <DialogSurface className={styles.dialogSm}>
          <DialogBody>
            <DialogTitle>Delete {ent.noun}?</DialogTitle>
            <DialogContent>This can't be undone.</DialogContent>
            <DialogActions>
              <DialogTrigger action="close">
                <Button>Cancel</Button>
              </DialogTrigger>
              <Button size="small" className={styles.danger} appearance="primary" disabled={busy} onClick={() => void remove()}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

/* =====================================================================
   Till setup dialogs (which register is this screen, and open its till)
   ===================================================================== */
const DeviceDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const ctx = useCtx();
  const [sel, setSel] = useState(ctx.device?.registerId ?? "");
  const { data, loading } = useLoad(async () => {
    if (!open) return null;
    const [regs, stores] = await Promise.all([crud<Row>("organization/registers").list({ pageSize: 200 }), crud<Row>("organization/stores").list({ pageSize: 200 })]);
    const names = new Map(stores.items.map((s) => [s.id, String(s.name)] as [string, string]));
    return regs.items.map((r) => ({ id: r.id, storeId: String(r.storeId), label: `${names.get(r.storeId) ?? "Store"} · ${r.name}` }));
  }, [open]);

  const save = () => {
    const r = data?.find((x) => x.id === sel);
    if (!r) return;
    ctx.setDevice({ storeId: r.storeId, registerId: r.id });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.dialogSm}>
        <DialogBody>
          <DialogTitle>Which till is this?</DialogTitle>
          <DialogContent>
            {loading && !data ? (
              <SkLine size={32} />
            ) : (data ?? []).length === 0 ? (
              <p className={styles.hint}>No registers yet. Add a store and a register under Setup first.</p>
            ) : (
              <Field label="Register" required>
                <Select value={sel} onChange={(_, d) => setSel(d.value)}>
                  <option value="">Select…</option>
                  {(data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button size="small" className={styles.primary} appearance="primary" disabled={!sel} onClick={save}>
              Use this register
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const TillDialog: React.FC<{ open: boolean; registerId: string; onClose: () => void; onOpened: () => void }> = ({ open, registerId, onClose, onOpened }) => {
  const ctx = useCtx();
  const [associateId, setAssociateId] = useState("");
  const [float, setFloat] = useState("0");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data, loading } = useLoad(async () => (open ? crud<Row>("organization/associates").list({ isActive: true, pageSize: 200 }) : null), [open]);

  const openTill = async () => {
    if (!associateId) return setErr("Choose who is on this till.");
    const amount = Number(float);
    if (!(amount >= 0)) return setErr("Enter the opening float.");
    setBusy(true);
    setErr(null);
    try {
      await cashApi.openTill({ registerId, associateId, openingFloat: amount, blindClose: true });
      ctx.say("Till opened");
      onOpened();
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.dialogSm}>
        <DialogBody>
          <DialogTitle>Open till</DialogTitle>
          <DialogContent>
            {loading && !data ? (
              <SkLine size={32} />
            ) : (
              <div className={styles.form}>
                <div className={styles.formFull}>
                  <Field label="Cashier" required>
                    <Select value={associateId} onChange={(_, d) => setAssociateId(d.value)}>
                      <option value="">Select…</option>
                      {(data?.items ?? []).map((a) => (
                        <option key={a.id} value={a.id}>
                          {String(a.displayName)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                <div className={styles.formFull}>
                  <Field label="Opening float">
                    <Input type="number" value={float} onChange={(_, d) => setFloat(d.value)} />
                  </Field>
                </div>
              </div>
            )}
            {err && (
              <div className={styles.formErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button size="small" className={styles.primary} appearance="primary" disabled={busy} onClick={() => void openTill()}>
              Open till
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* =====================================================================
   Payment dialog
   ===================================================================== */
const PayDialog: React.FC<{
  sale: SaleDto;
  tenders: Row[];
  onClose: () => void;
  onUpdate: (s: SaleDto) => void;
  onDone: (s: SaleDto, change: number) => void;
}> = ({ sale, tenders, onClose, onUpdate, onDone }) => {
  const ctx = useCtx();
  const [code, setCode] = useState<string>(tenders.find((t) => t.kind === "Cash")?.code ?? tenders[0]?.code ?? "");
  const [tendered, setTendered] = useState("");
  const [ref, setRef] = useState("");
  const [auth, setAuth] = useState("");
  const [pin, setPin] = useState("");
  const [points, setPoints] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  const cur = sale.currencyCode;
  const remaining = Math.max(0, sale.total + sale.roundingAdjustment - sale.amountPaid);
  const tender = tenders.find((t) => t.code === code);
  const kind = String(tender?.kind ?? "");
  const stored = kind === "GiftCard" || kind === "StoreCredit" || kind === "Voucher";
  const quick = [remaining, Math.ceil(remaining / 1000) * 1000, Math.ceil(remaining / 5000) * 5000, Math.ceil(remaining / 10000) * 10000].filter((x, i, a) => x > 0 && a.indexOf(x) === i);

  const pay = async () => {
    if (!tender) return setErr("Choose a payment method.");
    const body: PaymentInput = { tenderCode: String(tender.code) };
    if (kind === "Cash") {
      const t = Number(tendered);
      if (!(t > 0)) return setErr("Enter the cash received.");
      body.tenderedAmount = t;
      body.tenderCurrency = cur;
    } else if (stored) {
      if (!ref.trim()) return setErr("Enter the card number.");
      body.storedValueNumber = ref.trim();
      if (pin) body.pin = pin;
    } else if (kind === "LoyaltyPoints") {
      const p = Number(points);
      if (!(p > 0)) return setErr("Enter the points to redeem.");
      body.loyaltyPoints = p;
    } else {
      if ((tender.requiresReference || kind === "Card") && !ref.trim()) return setErr("Enter the payment reference.");
      if (ref.trim()) body.reference = ref.trim();
      if (auth.trim()) body.externalAuthCode = auth.trim();
    }
    setPaying(true);
    setErr(null);
    try {
      const out = await saleApi.addPayment(sale.id, body);
      if (out.fullyPaid) {
        const done = await saleApi.complete(sale.id);
        onDone(done, Math.max(out.changeGiven, done.changeGiven));
      } else {
        onUpdate(await saleApi.get(sale.id));
        setTendered("");
        setRef("");
        setAuth("");
        setPin("");
        setPoints("");
        ctx.say(`${money(out.applied, cur)} taken, ${money(out.remaining, cur)} left to pay`);
      }
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setPaying(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Take payment, {money(remaining, cur)}
          </DialogTitle>
          <DialogContent>
            <div className={styles.methods} role="group" aria-label="Payment method">
              {tenders.map((t) => (
                <button key={t.id} type="button" className={`${styles.segBtn} ${code === t.code ? styles.segOn : ""}`} aria-pressed={code === t.code} onClick={() => (setCode(String(t.code)), setErr(null))}>
                  {String(t.name)}
                </button>
              ))}
            </div>
            <div className={styles.payBody}>
              {kind === "Cash" && (
                <>
                  <Field label="Cash received">
                    <Input type="number" value={tendered} onChange={(_, d) => setTendered(d.value)} />
                  </Field>
                  <div className={styles.quick}>
                    {quick.map((x) => (
                      <Button key={x} size="small" onClick={() => setTendered(String(x))}>
                        {money(x, cur)}
                      </Button>
                    ))}
                  </div>
                  <div className={styles.change}>Change due: {money(Math.max(0, (Number(tendered) || 0) - remaining), cur)}</div>
                </>
              )}
              {stored && (
                <>
                  <Field label="Card number" required>
                    <Input value={ref} onChange={(_, d) => setRef(d.value)} />
                  </Field>
                  <Field label="PIN (if the card has one)">
                    <Input type="password" value={pin} onChange={(_, d) => setPin(d.value)} />
                  </Field>
                </>
              )}
              {kind === "LoyaltyPoints" && (
                <Field label="Points to redeem" required>
                  <Input type="number" value={points} onChange={(_, d) => setPoints(d.value)} />
                </Field>
              )}
              {kind !== "Cash" && !stored && kind !== "LoyaltyPoints" && tender && (
                <>
                  <p className={styles.hint}>Confirm the {money(remaining, cur)} payment was approved on the terminal or your bank app, then enter its details.</p>
                  <Field label="Payment reference" required={!!tender.requiresReference || kind === "Card"}>
                    <Input value={ref} onChange={(_, d) => setRef(d.value)} />
                  </Field>
                  {kind === "Card" && (
                    <Field label="Approval code">
                      <Input value={auth} onChange={(_, d) => setAuth(d.value)} />
                    </Field>
                  )}
                </>
              )}
            </div>
            {err && (
              <div className={styles.formErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button size="small" className={styles.primary} appearance="primary" disabled={paying || !tender} onClick={() => void pay()}>
              {paying ? "Processing…" : "Complete payment"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* =====================================================================
   Point of sale
   ===================================================================== */
const LOW_STOCK = 10;

const PosPage: React.FC = () => {
  const ctx = useCtx();
  const device = ctx.device;
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [tillOpen, setTillOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [sale, setSale] = useState<SaleDto | null>(null);
  const [customerId, setCustomerId] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [heldOpen, setHeldOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [receipt, setReceipt] = useState<{ sale: SaleDto; change: number } | null>(null);

  const products = useLoad(() => crud<Row>("catalog/products").list({ status: "Active", pageSize: 200 }), []);
  const categories = useLoad(() => crud<Row>("catalog/categories").list({ pageSize: 200 }), []);
  const customers = useLoad(() => crud<Row>("customers/customers").list({ pageSize: 200 }), []);
  const tenders = useLoad(() => crud<Row>("payments/tender-types").list({ isActive: true, pageSize: 100 }), []);
  const stock = useLoad(async () => (device ? crud<Row>("inventory/stock").list({ storeId: device.storeId, pageSize: 200 }) : null), [device?.storeId]);
  const till = useLoad(async () => (device ? crud<Row>("cash/till-sessions").list({ registerId: device.registerId, status: "Open", pageSize: 1 }) : null), [device?.registerId]);
  const where = useLoad(async () => {
    if (!device) return null;
    const [r, s] = await Promise.all([crud<Row>("organization/registers").get(device.registerId), crud<Row>("organization/stores").get(device.storeId)]);
    return `${s.name} · ${r.name}`;
  }, [device?.registerId]);
  const held = useLoad(async () => (heldOpen ? crud<Row>("pos/sales").list({ status: "Suspended", storeId: device?.storeId, pageSize: 50 }) : null), [heldOpen, device?.storeId]);
  useLive(
    (e) => e.module === "inventory" || e.module === "cash",
    () => {
      stock.reload();
      till.reload();
    },
  );

  useEffect(() => {
    if (!device) setDeviceOpen(true);
  }, [device]);

  const tillRow = till.data?.items[0] ?? null;
  const available = useMemo(() => {
    const m = new Map<string, number>();
    (stock.data?.items ?? []).forEach((s) => m.set(String(s.productId), (m.get(String(s.productId)) ?? 0) + num(s.qtyOnHand) - num(s.qtyReserved)));
    return m;
  }, [stock.data]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      ctx.say(errorMessage(e), true);
    } finally {
      setBusy(false);
    }
  };

  const ensureSale = async (): Promise<SaleDto> => {
    if (sale) return sale;
    if (!device) throw new Error("Choose which till this is first.");
    const s = await saleApi.start({ registerId: device.registerId, customerId: customerId || null });
    setSale(s);
    return s;
  };

  const add = (p: Row) =>
    void run(async () => {
      const s = await ensureSale();
      setSale(await saleApi.addItem(s.id, { productId: p.id, quantity: 1 }));
    });

  const setQty = (lineId: string, qty: number) =>
    void run(async () => {
      if (!sale) return;
      setSale(qty <= 0 ? await saleApi.voidLine(sale.id, lineId, "Removed by cashier") : await saleApi.setQuantity(sale.id, lineId, qty));
    });

  const changeCustomer = (id: string) => {
    setCustomerId(id);
    if (!sale) return;
    void run(async () => setSale(await saleApi.attachCustomer(sale.id, id || null)));
  };

  const applyCoupon = () =>
    void run(async () => {
      const code = promoInput.trim();
      if (!code) return;
      const s = await ensureSale();
      setSale(await saleApi.addCoupon(s.id, code));
      setPromoInput("");
      ctx.say(`Coupon ${code} applied`);
    });

  const hold = () =>
    void run(async () => {
      if (!sale) return;
      await saleApi.suspend(sale.id);
      ctx.say(`${sale.receiptNo} held`);
      setSale(null);
    });

  const clear = () =>
    void run(async () => {
      if (!sale) return;
      await saleApi.void(sale.id, "Cleared by cashier");
      setSale(null);
    });

  const resume = (id: string) =>
    void run(async () => {
      setSale(await saleApi.resume(id));
      setHeldOpen(false);
    });

  const openPay = () => {
    if (!sale || !activeLines.length) return;
    if (!tillRow) return ctx.say("Open the till before taking payment.", true);
    setPayOpen(true);
  };

  const activeLines = (sale?.lines ?? []).filter((l) => l.status === "Active");
  const itemCount = activeLines.reduce((n, l) => n + num(l.quantity), 0);
  const cur = sale?.currencyCode ?? ctx.currency;
  const owed = sale ? Math.max(0, sale.total + sale.roundingAdjustment - sale.amountPaid) : 0;
  const coupons = (sale?.couponCodesCsv ?? "").split(",").filter(Boolean);
  const stockReady = !!stock.data;

  const visible = (products.data?.items ?? []).filter((p) => (cat === "All" || p.categoryId === cat) && (!q.trim() || `${p.name} ${p.sku}`.toLowerCase().includes(q.trim().toLowerCase())));

  return (
    <div className={styles.pos}>
      <section className={styles.catalog} aria-label="Menu">
        <div className={styles.posTop}>
          <SearchBox className={styles.search} placeholder="Search items or SKU" value={q} onChange={(_, d) => setQ(d.value)} />
          <Button size="small" icon={<Desktop20Regular />} onClick={() => setDeviceOpen(true)}>
            {where.data ?? "Choose till"}
          </Button>
          {device && !tillRow && !till.loading && (
            <Button size="small" className={styles.primary} appearance="primary" onClick={() => setTillOpen(true)}>
              Open till
            </Button>
          )}
          <Button size="small" icon={<Pause20Regular />} onClick={() => setHeldOpen(true)}>
            Held orders
          </Button>
        </div>
        <TabList className={styles.cats} selectedValue={cat} onTabSelect={(_, d) => setCat(String(d.value))} size="small">
          <Tab value="All">All</Tab>
          {(categories.data?.items ?? []).map((c) => (
            <Tab key={c.id} value={c.id}>
              {String(c.name)}
            </Tab>
          ))}
        </TabList>
        <div className={styles.products}>
          {products.loading && !products.data &&
            Array.from({ length: 8 }, (_, i) => (
              <div key={i} className={styles.product} aria-hidden="true">
                <SkLine size={72} />
                <SkLine w="80%" />
                <SkLine w="50%" size={12} />
              </div>
            ))}
          {visible.map((p) => {
            const avail = available.get(p.id) ?? 0;
            const out = stockReady && avail <= 0;
            const low = stockReady && !out && avail <= LOW_STOCK;
            const inCart = activeLines.filter((l) => l.productId === p.id).reduce((n, l) => n + num(l.quantity), 0);
            return (
              <button key={p.id} type="button" className={styles.product} disabled={busy} onClick={() => add(p)} aria-label={`Add ${String(p.name)}, ${money(num(p.basePrice))}`}>
                <img src={foodImage} alt="" className={styles.productImg} />
                <span className={styles.productName}>{String(p.name)}</span>
                <span className={styles.productMeta}>{String(p.sku)}</span>
                <span className={styles.productFoot}>
                  <strong>{money(num(p.basePrice))}</strong>
                  {out ? <Pill text="Out of stock" /> : low ? <Pill text="Low stock" /> : inCart > 0 ? <span className={styles.inCart}>{inCart} in order</span> : null}
                </span>
              </button>
            );
          })}
          {!products.loading && visible.length === 0 && <div className={styles.empty}>{products.error ?? "No items match."}</div>}
        </div>
      </section>

      <aside className={styles.cart} aria-label="Current order">
        <div className={styles.cartHead}>
          <strong>Current order</strong>
          {till.loading && !till.data ? <SkLine w="80px" size={16} /> : <Pill text={tillRow ? "Open" : "Closed"} />}
        </div>
        <div className={styles.cartFields}>
          <Select value={customerId} onChange={(_, d) => changeCustomer(d.value)} aria-label="Customer" disabled={busy}>
            <option value="">Walk-in customer</option>
            {(customers.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {String(c.displayName)}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles.cartLines}>
          {activeLines.map((l) => (
            <div key={l.id} className={styles.cartLine}>
              <div className={styles.cartName}>
                <span>{l.description}</span>
                <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} aria-label={`Remove ${l.description}`} disabled={busy} onClick={() => setQty(l.id, 0)} />
              </div>
              <div className={styles.cartQty}>
                <div className={styles.stepper}>
                  <Button size="small" shape="circular" icon={<Subtract16Regular />} aria-label={`Decrease ${l.description}`} disabled={busy} onClick={() => setQty(l.id, num(l.quantity) - 1)} />
                  <span className={styles.qty} aria-label={`${l.description} quantity`}>
                    {num(l.quantity)}
                  </span>
                  <Button size="small" shape="circular" icon={<Add16Regular />} aria-label={`Increase ${l.description}`} disabled={busy} onClick={() => setQty(l.id, num(l.quantity) + 1)} />
                </div>
                <strong>{money(l.lineTotal, cur)}</strong>
              </div>
            </div>
          ))}
          {busy && activeLines.length === 0 && <SkLine size={32} />}
          {!busy && activeLines.length === 0 && <div className={styles.cartEmpty}>Tap an item to start an order.</div>}
        </div>

        <div className={styles.promoRow}>
          <Input className={styles.promoInput} placeholder="Coupon code" value={promoInput} onChange={(_, d) => setPromoInput(d.value)} onKeyDown={(e) => e.key === "Enter" && applyCoupon()} />
          <Button size="small" disabled={busy || !promoInput.trim()} onClick={applyCoupon}>
            Apply
          </Button>
        </div>
        {coupons.length > 0 && (
          <div className={styles.promoRow}>
            {coupons.map((c) => (
              <Button
                key={c}
                size="small"
                appearance="subtle"
                icon={<Dismiss16Regular />}
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    if (sale) setSale(await saleApi.removeCoupon(sale.id, c));
                  })
                }
              >
                {c}
              </Button>
            ))}
          </div>
        )}
        <dl className={styles.sums}>
          <div>
            <dt>Subtotal ({itemCount} items)</dt>
            <dd>{money(sale?.subTotal ?? 0, cur)}</dd>
          </div>
          {num(sale?.discountTotal) > 0 && (
            <div>
              <dt>Discount</dt>
              <dd>-{money(sale?.discountTotal ?? 0, cur)}</dd>
            </div>
          )}
          <div>
            <dt>Tax{sale?.taxInclusive ? " (included)" : ""}</dt>
            <dd>{money(sale?.taxTotal ?? 0, cur)}</dd>
          </div>
          {num(sale?.roundingAdjustment) !== 0 && (
            <div>
              <dt>Rounding</dt>
              <dd>{money(sale?.roundingAdjustment ?? 0, cur)}</dd>
            </div>
          )}
          <div className={styles.grand}>
            <dt>Total</dt>
            <dd>{money((sale?.total ?? 0) + (sale?.roundingAdjustment ?? 0), cur)}</dd>
          </div>
        </dl>
        <div className={styles.cartActions}>
          <Button size="small" icon={<Delete16Regular />} disabled={!sale || busy} onClick={clear}>
            Clear
          </Button>
          <Button size="small" icon={<Pause20Regular />} disabled={!sale || !activeLines.length || busy} onClick={hold}>
            Hold
          </Button>
          <Button size="small" className={`${styles.primary} ${styles.payBtn}`} appearance="primary" disabled={!activeLines.length || busy} onClick={openPay}>
            Pay {activeLines.length ? money(owed, cur) : ""}
          </Button>
        </div>
      </aside>

      <DeviceDialog open={deviceOpen} onClose={() => setDeviceOpen(false)} />
      {device && <TillDialog open={tillOpen} registerId={device.registerId} onClose={() => setTillOpen(false)} onOpened={till.reload} />}

      {payOpen && sale && (
        <PayDialog
          sale={sale}
          tenders={tenders.data?.items ?? []}
          onClose={() => setPayOpen(false)}
          onUpdate={setSale}
          onDone={(done, change) => {
            setPayOpen(false);
            setSale(null);
            setReceipt({ sale: done, change });
          }}
        />
      )}

      {/* held orders */}
      <Dialog open={heldOpen} onOpenChange={(_, d) => setHeldOpen(d.open)}>
        <DialogSurface className={styles.dialog}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Held orders
            </DialogTitle>
            <DialogContent>
              {held.loading && !held.data && (
                <>
                  <SkLine size={32} />
                  <SkLine size={32} />
                </>
              )}
              {held.data && held.data.items.length === 0 && <p className={styles.hint}>No orders on hold.</p>}
              {(held.data?.items ?? []).map((o) => (
                <div key={o.id} className={styles.heldRow}>
                  <div>
                    <strong>{String(o.receiptNo)}</strong>
                    <div className={styles.hint}>
                      {fdt(String(o.suspendedAtUtc ?? o.startedAtUtc))} · {money(num(o.total), String(o.currencyCode))}
                    </div>
                  </div>
                  <Button className={styles.primary} appearance="primary" size="small" disabled={busy || !!sale} onClick={() => resume(o.id)}>
                    Resume
                  </Button>
                </div>
              ))}
              {sale && <p className={styles.hint}>Hold or clear the current order before resuming another.</p>}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* receipt */}
      <Dialog open={!!receipt} onOpenChange={(_, d) => !d.open && setReceipt(null)}>
        <DialogSurface className={styles.dialogSm}>
          <DialogBody>
            <DialogTitle>Payment complete</DialogTitle>
            <DialogContent>
              {receipt && (
                <div className={styles.receipt}>
                  <div className={styles.receiptHead}>
                    <strong>{receipt.sale.receiptNo}</strong>
                    <span>{fdt(receipt.sale.completedAtUtc ?? receipt.sale.startedAtUtc)}</span>
                  </div>
                  <div className={styles.lines}>
                    {receipt.sale.lines
                      .filter((l) => l.status === "Active")
                      .map((l) => (
                        <div key={l.id} className={styles.lineRow}>
                          <span>
                            {num(l.quantity)} × {l.description}
                          </span>
                          <span>{money(l.lineTotal, receipt.sale.currencyCode)}</span>
                        </div>
                      ))}
                    {receipt.sale.discountTotal > 0 && (
                      <div className={styles.lineRow}>
                        <span>Discount</span>
                        <span>-{money(receipt.sale.discountTotal, receipt.sale.currencyCode)}</span>
                      </div>
                    )}
                    <div className={styles.lineRow}>
                      <span>Tax{receipt.sale.taxInclusive ? " (included)" : ""}</span>
                      <span>{money(receipt.sale.taxTotal, receipt.sale.currencyCode)}</span>
                    </div>
                    <div className={`${styles.lineRow} ${styles.grandLine}`}>
                      <span>Total</span>
                      <span>{money(receipt.sale.total + receipt.sale.roundingAdjustment, receipt.sale.currencyCode)}</span>
                    </div>
                    {receipt.sale.payments.map((p) => (
                      <div key={p.id} className={styles.lineRow}>
                        <span>Paid by {p.tenderCode}</span>
                        <span>{money(p.amount, receipt.sale.currencyCode)}</span>
                      </div>
                    ))}
                    {receipt.change > 0 && (
                      <div className={styles.lineRow}>
                        <span>Change</span>
                        <span>{money(receipt.change, receipt.sale.currencyCode)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger action="close">
                <Button size="small" className={styles.primary} appearance="primary">
                  New order
                </Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

/* =====================================================================
   Order board (online and pickup orders, from the dashboard feed)
   ===================================================================== */
const BOARD_COLS = [
  { id: "Pending", label: "New" },
  { id: "Picking", label: "Picking" },
  { id: "Packed", label: "Packed" },
  { id: "ReadyForPickup", label: "Ready" },
  { id: "CustomerArrived", label: "Customer arrived" },
];
const isPickup = (t: string) => t === "Bopis" || t === "Curbside";

const boardAction = (c: OrderBoardCard): { label: string; done: string; run: () => Promise<unknown> } | null => {
  const f = c.fulfillmentId;
  if (!f) return null;
  switch (c.status) {
    case "Pending":
      return { label: "Start picking", done: "picking started", run: () => orderApi.startPicking(f) };
    case "Picking":
      return { label: "Mark packed", done: "packed", run: () => orderApi.markPacked(f) };
    case "Packed":
      return isPickup(c.fulfillmentType)
        ? { label: "Ready for pickup", done: "ready for pickup", run: () => orderApi.markReady(f) }
        : { label: "Ship", done: "shipped", run: () => orderApi.ship(f) };
    case "ReadyForPickup":
    case "CustomerArrived":
      return { label: "Hand over", done: "handed over", run: () => orderApi.handOver(f, c.pickupCode) };
    default:
      return null;
  }
};

const BoardPage: React.FC = () => {
  const ctx = useCtx();
  const storeId = ctx.device?.storeId;
  const [kind, setKind] = useState("All");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const { data, loading, error, reload } = useLoad(() => dashboard.orderBoard(storeId, 100), [storeId]);
  useLive((e) => e.module === "orders" || e.module === "pos", reload);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 30000);
    return () => window.clearInterval(t);
  }, []);

  const advance = async (c: OrderBoardCard) => {
    const a = boardAction(c);
    if (!a) return;
    setBusyId(c.orderId);
    try {
      await a.run();
      ctx.say(`${c.orderNo} ${a.done}`);
      reload();
    } catch (e) {
      ctx.say(errorMessage(e), true);
    } finally {
      setBusyId(null);
    }
  };

  const cards = (data ?? []).filter((c) => kind === "All" || (kind === "Pickup" ? isPickup(c.fulfillmentType) : !isPickup(c.fulfillmentType)));

  return (
    <>
      <div className={styles.filters}>
        <TabList selectedValue={kind} onTabSelect={(_, d) => setKind(String(d.value))} size="small">
          {["All", "Pickup", "Delivery"].map((s) => (
            <Tab key={s} value={s}>
              {s}
            </Tab>
          ))}
        </TabList>
      </div>
      {error && !data && <ErrorCard message={error} onRetry={reload} />}
      <div className={styles.kds}>
        {BOARD_COLS.map((col) => {
          const list = cards.filter((c) => c.status === col.id).sort((a, b) => a.placedAtUtc.localeCompare(b.placedAtUtc));
          return (
            <section key={col.id} className={styles.kdsCol} aria-label={col.label}>
              <header className={styles.kdsHead}>
                <span>{col.label}</span>
                <span className={styles.badge}>{loading && !data ? "…" : list.length}</span>
              </header>
              <div className={styles.kdsBody}>
                {loading && !data &&
                  [0, 1].map((i) => (
                    <article key={i} className={styles.ticket} aria-hidden="true">
                      <SkLine w="60%" />
                      <SkLine w="40%" size={12} />
                      <SkLine />
                      <SkLine w="50%" size={32} />
                    </article>
                  ))}
                {list.map((c) => {
                  const mins = minutesSince(c.placedAtUtc);
                  const act = boardAction(c);
                  return (
                    <article key={c.orderId} className={`${styles.ticket} ${mins >= 20 && col.id !== "ReadyForPickup" ? styles.late : ""}`}>
                      <div className={styles.ticketHead}>
                        <strong>{c.orderNo}</strong>
                        <span className={styles.timer}>{mins} min</span>
                      </div>
                      <div className={styles.hint}>
                        {c.customerName} · {spaced(c.fulfillmentType)}
                        {c.place ? `, ${c.place}` : ""}
                      </div>
                      <ul className={styles.ticketLines}>
                        <li>
                          <strong>{c.itemCount}</strong> {c.itemCount === 1 ? "item" : "items"}
                        </li>
                        {c.pickupCode && <li>Pickup code {c.pickupCode}</li>}
                      </ul>
                      {act && (
                        <Button className={styles.primary} appearance="primary" size="small" disabled={busyId === c.orderId} onClick={() => void advance(c)}>
                          {act.label}
                        </Button>
                      )}
                    </article>
                  );
                })}
                {!loading && list.length === 0 && <div className={styles.empty}>No orders.</div>}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

/* =====================================================================
   Overview and reports
   ===================================================================== */
const Dashboard: React.FC = () => {
  const ctx = useCtx();
  const storeId = ctx.device?.storeId;
  const { data, loading, error, reload } = useLoad(() => dashboard.overview(storeId), [storeId]);
  useLive((e) => e.event === "analytics.live_kpis" || e.module === "pos" || e.module === "orders", reload);

  if (error && !data) return <div className={styles.dash}><ErrorCard message={error} onRetry={reload} /></div>;
  if (!data)
    return (
      <div className={styles.dash} aria-busy="true">
        <TilesSkeleton count={5} />
        <div className={styles.dashGrid}>
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );

  const cur = data.currencyCode;
  const change = data.netSalesYesterday > 0 ? ((data.netSales - data.netSalesYesterday) / data.netSalesYesterday) * 100 : null;
  const hoursWith = data.byHour.map((h) => h.hour);
  const from = Math.min(8, ...hoursWith);
  const to = Math.max(22, ...hoursWith);
  const hours = Array.from({ length: to - from + 1 }, (_, i) => from + i).map((h) => ({ label: pad(h), value: data.byHour.find((x) => x.hour === h)?.netSales ?? 0 }));

  const tiles = [
    { label: "Sales today", value: money(data.netSales, cur), note: `${data.orders} orders${change === null ? "" : ` · ${change >= 0 ? "+" : ""}${change.toFixed(1)}% vs yesterday`}`, to: "sales" },
    { label: "Average order", value: money(data.averageOrder, cur), note: "Per completed sale", to: "reports" },
    { label: "On the order board", value: String(data.openBoardOrders), note: "Open online and pickup orders", to: "board" },
    { label: "Low or out of stock", value: String(data.lowStockItems), note: "Items running short", to: "stock" },
    { label: "Open tills", value: String(data.openShifts), note: "Cashiers on a till", to: "tills" },
  ];

  return (
    <div className={styles.dash}>
      <div className={styles.tiles}>
        {tiles.map((t) => (
          <button key={t.label} type="button" className={styles.tile} onClick={() => ctx.go(t.to)}>
            <span className={styles.tileLabel}>{t.label}</span>
            <span className={styles.tileValue}>{t.value}</span>
            <span className={styles.tileNote}>{t.note}</span>
          </button>
        ))}
      </div>
      <div className={styles.dashGrid}>
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Sales by hour today</h3>
          <Columns items={hours} unit="Sales by hour" cur={cur} />
        </section>
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Top sellers today</h3>
          <Bars cur={cur} items={data.topSellers.map((t) => ({ label: t.description, value: t.revenue, note: `${t.units} sold` }))} />
        </section>
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Payment methods today</h3>
          <Bars cur={cur} tone="fillSlate" items={data.paymentMethods.map((p) => ({ label: p.name, value: p.amount }))} />
        </section>
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Needs attention</h3>
          <ul className={styles.todo}>
            {data.needsAttention.slice(0, 6).map((a, i) => (
              <li key={`${a.kind}${i}`}>
                <button type="button" className={styles.todoBtn} onClick={() => ctx.go(/stock/i.test(a.kind) ? "stock" : /alert|loss/i.test(a.kind) ? "alerts" : "dashboard")}>
                  <span>{a.title}</span>
                  <Pill text={spaced(a.severity)} />
                </button>
              </li>
            ))}
            {data.needsAttention.length === 0 && <li className={styles.empty}>Nothing needs attention.</li>}
          </ul>
        </section>
      </div>
      {loading && <span className={styles.hint}>Updating…</span>}
    </div>
  );
};

const ReportsPage: React.FC = () => {
  const ctx = useCtx();
  const storeId = ctx.device?.storeId;
  const [range, setRange] = useState("7");
  const days = Number(range);
  const { data, error, reload } = useLoad(() => dashboard.salesReport(off(-(days - 1)), off(0), storeId), [range, storeId]);

  return (
    <div className={styles.dash}>
      <div className={styles.filters}>
        <Select className={styles.statusSel} value={range} onChange={(_, d) => setRange(d.value)} aria-label="Period">
          <option value="1">Today</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
        </Select>
      </div>
      {error && !data && <ErrorCard message={error} onRetry={reload} />}
      {!data && !error && (
        <>
          <TilesSkeleton />
          <div className={styles.dashGrid}>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </>
      )}
      {data && (
        <>
          <div className={styles.tiles}>
            {[
              { label: "Net sales", value: money(data.netSales, data.currencyCode), note: `${data.orders} orders` },
              { label: "Average order", value: money(data.averageOrder, data.currencyCode), note: "Per completed sale" },
              { label: "Gross sales", value: money(data.grossSales, data.currencyCode), note: "Before discounts" },
              { label: "Discounts given", value: money(data.discounts, data.currencyCode), note: "Promotions and coupons" },
              { label: "Tax", value: money(data.tax, data.currencyCode), note: "Collected on sales" },
            ].map((t) => (
              <div key={t.label} className={`${styles.tile} ${styles.tileStatic}`}>
                <span className={styles.tileLabel}>{t.label}</span>
                <span className={styles.tileValue}>{t.value}</span>
                <span className={styles.tileNote}>{t.note}</span>
              </div>
            ))}
          </div>
          <div className={styles.dashGrid}>
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Daily sales</h3>
              <Columns cur={data.currencyCode} unit="Daily sales" items={data.daily.map((d) => ({ label: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`, value: d.netSales }))} />
            </section>
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Sales by category</h3>
              <Bars cur={data.currencyCode} items={data.byCategory.map((c) => ({ label: c.name, value: c.amount }))} />
            </section>
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Sales by payment method</h3>
              <Bars cur={data.currencyCode} tone="fillSlate" items={data.byPaymentMethod.map((c) => ({ label: c.name, value: c.amount }))} />
            </section>
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Sales by cashier</h3>
              <Bars cur={data.currencyCode} tone="fillSlate" items={data.byCashier.map((c) => ({ label: c.name, value: c.amount }))} />
            </section>
            <section className={`${styles.card} ${styles.wide}`}>
              <h3 className={styles.cardTitle}>Best-selling products</h3>
              <table className={styles.grid}>
                <thead>
                  <tr>
                    <th>
                      <span className={styles.th}>Product</span>
                    </th>
                    <th className={styles.right}>
                      <span className={styles.th}>Quantity</span>
                    </th>
                    <th className={styles.right}>
                      <span className={styles.th}>Revenue</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.bestSellers.map((t) => (
                    <tr key={t.productId}>
                      <td>{t.description}</td>
                      <td className={styles.right}>{t.units}</td>
                      <td className={styles.right}>{money(t.revenue, data.currencyCode)}</td>
                    </tr>
                  ))}
                  {data.bestSellers.length === 0 && (
                    <tr>
                      <td colSpan={3} className={styles.empty}>
                        No sales in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

/* =====================================================================
   Shell
   ===================================================================== */
const Retails: React.FC = () => {
  const [page, setPage] = useState("pos");
  const [rail, setRail] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const [currency, setCurrencyState] = useState("NGN");
  const [device, setDeviceState] = useState<Device | null>(() => {
    try {
      const s = localStorage.getItem(DEVICE_KEY);
      return s ? (JSON.parse(s) as Device) : null;
    } catch {
      return null;
    }
  });

  const settings = useLoad(() => crud<Row>("admin/tenant-settings").list({ pageSize: 1 }), []);
  useEffect(() => {
    const c = settings.data?.items[0]?.baseCurrency as string | undefined;
    if (c) {
      setCurrency(c);
      setCurrencyState(c);
    }
  }, [settings.data]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const say = useCallback((msg: string, err = false) => setToast({ msg, err }), []);
  const go = useCallback((id: string) => {
    setPage(id);
    setNavOpen(false);
  }, []);
  const setDevice = useCallback((d: Device | null) => {
    setDeviceState(d);
    try {
      if (d) localStorage.setItem(DEVICE_KEY, JSON.stringify(d));
      else localStorage.removeItem(DEVICE_KEY);
    } catch {
      /* storage may be unavailable; the choice then lasts until the page closes */
    }
  }, []);
  const ctx = useMemo<Ctx>(() => ({ currency, device, setDevice, say, go }), [currency, device, setDevice, say, go]);

  const ent = ENTITIES.find((e) => e.id === page) ?? (ENTITIES[0] as Entity);
  const groups = ENTITIES.reduce<{ name: string; items: Entity[] }[]>((acc, e) => {
    const g = acc.find((x) => x.name === e.group);
    if (g) g.items.push(e);
    else acc.push({ name: e.group, items: [e] });
    return acc;
  }, []).sort((a, b) => GROUP_ORDER.indexOf(a.name) - GROUP_ORDER.indexOf(b.name));
  const compact = ent.page === "pos" || ent.page === "board";

  return (
    <RetailContext.Provider value={ctx}>
      <div className={styles.gU1Tf}>
        <div className={styles.jUsfu}>
          <div className={styles.QIl4y}>
            <FluentProvider theme={webLightTheme} className={styles.root}>
              <aside className={`${styles.side} ${rail ? styles.sideRail : ""} ${navOpen ? styles.sideOpen : ""}`} aria-label="Retail navigation">
                <div className={styles.brand}>
                  <span className={styles.brandMark}>
                    <img src={retailsLogo} alt="" width={22} height={22} />
                  </span>
                  <span className={styles.brandName}>Cilpron Retail</span>
                  <Button className={styles.railBtn} appearance="subtle" size="small" icon={<PanelLeft20Regular />} aria-label={rail ? "Expand sidebar" : "Collapse sidebar"} onClick={() => setRail(!rail)} />
                </div>
                <nav className={styles.nav}>
                  {groups.map((g) => (
                    <div key={g.name} className={styles.navGroup}>
                      <div className={styles.navLabel}>{g.name}</div>
                      {g.items.map((e) => (
                        <button key={e.id} type="button" title={e.title} className={`${styles.navItem} ${page === e.id ? styles.navActive : ""}`} aria-current={page === e.id ? "page" : undefined} onClick={() => go(e.id)}>
                          {e.icon}
                          <span className={styles.navText}>{e.title}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </nav>
              </aside>
              {navOpen && <div className={styles.scrim} onClick={() => setNavOpen(false)} aria-hidden="true" />}

              <main className={styles.main}>
                <header className={`${styles.head} ${compact ? styles.headCompact : ""}`}>
                  <Button size="small" className={styles.navToggle} appearance="subtle" icon={<Navigation20Regular />} aria-label="Open navigation" onClick={() => setNavOpen(true)} />
                  <div>
                    <div className={styles.crumb}>
                      Retail › {ent.group} › {ent.title}
                    </div>
                    <h1 className={styles.title}>{ent.title}</h1>
                    {!compact && <p className={styles.sub}>{ent.subtitle}</p>}
                  </div>
                </header>

                {ent.page === "pos" && <PosPage />}
                {ent.page === "board" && <BoardPage />}
                {ent.page === "dash" && <Dashboard />}
                {ent.page === "reports" && <ReportsPage />}
                {ent.page === "grid" && <EntityPage key={ent.id} ent={ent} />}
              </main>

              {toast && (
                <div className={`${styles.toast} ${toast.err ? styles.toastErr : ""}`} role="status" aria-live="polite">
                  {toast.msg}
                </div>
              )}
            </FluentProvider>
          </div>
        </div>
      </div>
    </RetailContext.Provider>
  );
};

export default Retails;